import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Lightbulb, Loader2, ArrowRight, Target } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { achievementService } from '@/services/achievementService';
import { Cashpurr } from '@/components/ui/cashpurr';
import { type IdeaFormData } from '@/utils/validation-transformer';
import { useNotifications } from '@/hooks/useNotifications';

// Using IdeaFormData from transformer directly

const IdeaValidation: React.FC = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  useNotifications(); // Initialize notification system
  
  const [ideaData, setIdeaData] = useState<IdeaFormData>({
    idea_description: '',
    target_audience: '',
    problem_solved: '',
    revenue_model: '',
    competition: '',
    why_pay: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [existingIdea, setExistingIdea] = useState<any>(null);

  useEffect(() => {
    if (user) {
      fetchExistingIdea();
    }
  }, [user]);

  const fetchExistingIdea = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('idea_assessments')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data && !error) {
        setExistingIdea(data);
        setIdeaData({
          idea_description: data.idea_description,
          target_audience: data.target_audience,
          problem_solved: data.problem_solved,
          revenue_model: data.revenue_model,
          competition: data.competition,
          why_pay: data.why_people_pay,
        });
        
        // Don't auto-redirect - let users access the form even with existing feedback
      }
    } catch (error) {
      console.error('Error fetching existing idea:', error);
    }
  };

  const updateIdeaData = (field: keyof IdeaFormData, value: string) => {
    setIdeaData(prev => ({ ...prev, [field]: value }));
  };

  const canSubmit = () => {
    return Object.values(ideaData).every(value => value.trim().length > 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !canSubmit()) return;
    
    setLoading(true);
    try {
      // Save idea to database
      const { data: savedIdea, error: saveError } = await supabase
        .from('idea_assessments')
        .upsert({
          user_id: user.id,
          idea_description: ideaData.idea_description,
          target_audience: ideaData.target_audience,
          problem_solved: ideaData.problem_solved,
          revenue_model: ideaData.revenue_model,
          competition: ideaData.competition,
          why_people_pay: ideaData.why_pay,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (saveError) throw saveError;

      // Check if AI feedback already exists in the idea_assessments table
      let aiFeedback;
      
      if (savedIdea.ai_feedback) {
        // Use existing feedback
        aiFeedback = savedIdea.ai_feedback;
        console.log('Using existing AI feedback for idea:', savedIdea.id);
      } else {
        // Generate new AI feedback
        console.log('Calling AI validation with data:', ideaData);
        const { data: feedback, error: aiError } = await supabase.functions.invoke('ai-validation', {
          body: ideaData
        });
        
        console.log('AI validation response:', { feedback, aiError });
        
        if (feedback?.feedback) {
          aiFeedback = feedback.feedback;
          
          // Update the idea_assessments table with AI feedback
          await supabase
            .from('idea_assessments')
            .update({
              ai_feedback: feedback.feedback,
              ai_ratings: feedback.feedback.ratings,
              ai_questions: feedback.feedback.questions,
              updated_at: new Date().toISOString(),
            })
            .eq('id', savedIdea.id);
        } else {
          throw new Error('Failed to generate AI feedback');
        }
      }

      if (aiFeedback) {
        // Award idea validation badge
        await achievementService.awardIdeaValidationBadge(user.id);

        // Redirect to validation results page
        navigate(`/app/validation-results/${savedIdea.id}`);

        toast({
          title: "Analysis Complete! 🎉",
          description: "Your idea has been validated. View the detailed results!",
        });
      } else {
        toast({
          title: "AI Analysis Failed",
          description: "Couldn't get AI feedback, but your idea was saved.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error submitting idea:', error);
      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center mb-4">
          <Cashpurr size="xl" mood="critical" />
        </div>
        <h1 className="text-4xl font-bold mb-2">Idea Assessment</h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Get critical feedback from AI investors and Cashpurr. Validate your business idea before investing time and money.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
        {/* Idea Form */}
        <div className="space-y-6">
          <Card className="h-fit">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2 text-2xl">
                <Target className="w-6 h-6" />
                <span>Your Business Idea</span>
              </CardTitle>
              <CardDescription className="text-base">
                Fill out all fields to get comprehensive AI feedback. Be specific and honest.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="idea_description" className="text-base font-medium">Idea Description</Label>
                  <Textarea
                    id="idea_description"
                    placeholder="Describe your business idea in detail..."
                    value={ideaData.idea_description}
                    onChange={(e) => updateIdeaData('idea_description', e.target.value)}
                    className="min-h-[120px] text-base"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="target_audience" className="text-base font-medium">Target Audience</Label>
                    <Input
                      id="target_audience"
                      placeholder="Who is this for?"
                      value={ideaData.target_audience}
                      onChange={(e) => updateIdeaData('target_audience', e.target.value)}
                      className="text-base"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="revenue_model" className="text-base font-medium">Revenue Model</Label>
                    <Input
                      id="revenue_model"
                      placeholder="How will you make money?"
                      value={ideaData.revenue_model}
                      onChange={(e) => updateIdeaData('revenue_model', e.target.value)}
                      className="text-base"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="problem_solved" className="text-base font-medium">Problem Solved</Label>
                  <Textarea
                    id="problem_solved"
                    placeholder="What problem does this solve?"
                    value={ideaData.problem_solved}
                    onChange={(e) => updateIdeaData('problem_solved', e.target.value)}
                    className="min-h-[100px] text-base"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="competition" className="text-base font-medium">Competition</Label>
                    <Input
                      id="competition"
                      placeholder="Who are your competitors?"
                      value={ideaData.competition}
                      onChange={(e) => updateIdeaData('competition', e.target.value)}
                      className="text-base"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="why_pay" className="text-base font-medium">Why Will People Pay?</Label>
                    <Input
                      id="why_pay"
                      placeholder="What makes people willing to pay?"
                      value={ideaData.why_pay}
                      onChange={(e) => updateIdeaData('why_pay', e.target.value)}
                      className="text-base"
                      required
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full text-lg py-3" 
                  disabled={!canSubmit() || loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      Get AI Feedback
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Right side - will show validation results on separate page */}
        <div className="space-y-6">
          <Card className="text-center py-12">
            <CardContent>
              <Target className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold mb-2">Ready to Assess Your Idea?</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Fill out the form on the left with your business idea details. Our AI will provide critical feedback and ratings to help you make informed decisions.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>


    </div>
  );
};

export default IdeaValidation;